import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Portal from "./Portal";
import ConfirmationModal from "./ConfirmationModal";
import Loader from "./Loader";
import userActions from "../../users/actions";
import ReactTooltip from "react-tooltip";
import { SIDEBAR_CARD_TOOLTIP_ID } from "./DashboardSwitcher";
import { navigateWithPrompts } from "../../../utils/navigationHelpers";
import AppLoaderOverlay from "./AppLoaderOverlay";
import { useDefaultProjectUpdate } from "../../dashboard/components/useDefaultProjectUpdate";

const FLYOUT_WIDTH = 250;
const FLYOUT_GAP = 8;

const DefaultProjectPicker = ({ activeProjectId, projectName, children }) => {
    const dispatch = useDispatch();
    const isFormDirty = useSelector(state => state.commonReducer?.isFormDirty);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const [pendingProject, setPendingProject] = useState(null);
    const [query, setQuery] = useState("");
    const wrapperRef = useRef(null);
    const searchInputRef = useRef(null);

    const close = () => {
        setOpen(false);
        setPosition(null);
        setQuery("");
    };

    const {
        projects,
        isProjectsLoading,
        isSwitching,
        applyProjectClients,
        updateDefaultProject,
        resolveRedirectsToAssetDashboard,
        isOnTargetDashboard
    } = useDefaultProjectUpdate({ onUpdated: close });

    useEffect(() => {
        if (!projects.length) dispatch(userActions.getAllProjectsDropdown());
    }, []);

    useEffect(() => {
        const handleOutsideClick = event => {
            if (isSwitching) return;
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close();
        };
        const handleKeyDown = event => {
            if (event.key === "Escape" && !isSwitching) close();
        };
        if (open) {
            document.addEventListener("mousedown", handleOutsideClick);
            document.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, isSwitching]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [projectName, open]);

    useEffect(() => {
        if (open && !isProjectsLoading) searchInputRef.current?.focus();
    }, [open, isProjectsLoading]);

    const togglePicker = event => {
        event.stopPropagation();
        ReactTooltip.hide();
        if (open) {
            close();
            return;
        }
        const card = wrapperRef.current?.getBoundingClientRect();
        const sidebar = document.getElementById("sidebar")?.getBoundingClientRect();
        if (!projects.length) dispatch(userActions.getAllProjectsDropdown());
        setPosition({
            top: Math.max(10, Math.min(card?.top ?? 10, window.innerHeight - 20)),
            left: Math.min((sidebar?.right ?? card?.right ?? 0) + FLYOUT_GAP, window.innerWidth - FLYOUT_WIDTH - FLYOUT_GAP)
        });
        setOpen(true);
    };

    const filteredProjects = query.trim()
        ? projects.filter(project => project.name?.toLowerCase().includes(query.trim().toLowerCase()))
        : projects;

    const handleSelect = project => {
        if (project.id?.toString() === activeProjectId?.toString()) {
            close();
            return;
        }
        const clientId = applyProjectClients(project.id);
        if (isFormDirty) close();
        navigateWithPrompts({
            isFormDirty,
            navigateAction: () => {
                if (isOnTargetDashboard()) {
                    updateDefaultProject(project.id, clientId);
                    close();
                } else {
                    setPendingProject({ ...project, clientId });
                }
            }
        });
    };

    return (
        <div
            className="project-name-box project-name-wrapper d-flex ml-2 mr-2 mb-0"
            ref={wrapperRef}
            role="button"
            tabIndex={0}
            aria-haspopup="listbox"
            aria-expanded={open}
            data-tip={
                open ? null : projectName ? `${projectName}<br/>Click to change default project` : "Click to change default project"
            }
            data-multiline={true}
            data-for={SIDEBAR_CARD_TOOLTIP_ID}
            onClick={togglePicker}
            onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    togglePicker(event);
                }
            }}
        >
            {children}
            <span className="default-project-edit-icon">
                <i className="fas fa-exchange-alt fa-rotate-90" />
            </span>
            {open && position && (
                <div className="dashboard-switcher-flyout project-picker-flyout" style={{ top: position.top, left: position.left }} role="listbox">
                    <div className="dashboard-switcher-flyout-title">Switch Project</div>
                    {!isProjectsLoading && (
                        <div className="project-picker-search">
                            <input
                                ref={searchInputRef}
                                type="search"
                                className="flyout-search-input"
                                placeholder="Search projects..."
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                onClick={event => event.stopPropagation()}
                                onKeyDown={event => {
                                    event.stopPropagation();
                                    if (event.key === "Escape") close();
                                }}
                            />
                        </div>
                    )}
                    {isProjectsLoading ? (
                        <div className="project-picker-loading">
                            <Loader />
                        </div>
                    ) : (
                        <div className="project-picker-list">
                            {!filteredProjects.length && <div className="flyout-empty">No projects found</div>}
                            {filteredProjects.map(project => (
                                <div
                                    key={project.id}
                                    className={`dashboard-switcher-option ${
                                        project.id?.toString() === activeProjectId?.toString() ? "selected" : ""
                                    }`}
                                    role="option"
                                    aria-selected={project.id?.toString() === activeProjectId?.toString()}
                                    tabIndex={0}
                                    title={project.name}
                                    onClick={() => handleSelect(project)}
                                    onKeyDown={event => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleSelect(project);
                                        }
                                    }}
                                >
                                    <span className="dashboard-switcher-option-label">{project.name}</span>
                                    {project.id?.toString() === activeProjectId?.toString() && <i className="fas fa-check" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {pendingProject && (
                <Portal
                    body={
                        <ConfirmationModal
                            type="cancel"
                            heading={`Changing the default project will redirect you to the ${
                                resolveRedirectsToAssetDashboard() ? "Asset Dashboard" : "FCA Dashboard"
                            }`}
                            message={"Are you sure that you need to update default project?"}
                            onNo={() => setPendingProject(null)}
                            onYes={() => {
                                const project = pendingProject;
                                setPendingProject(null);
                                updateDefaultProject(project.id, project.clientId);
                                close();
                            }}
                        />
                    }
                    onCancel={() => setPendingProject(null)}
                />
            )}
            {isSwitching && <AppLoaderOverlay message="Switching project..." />}
        </div>
    );
};

export default DefaultProjectPicker;
